import React, { useCallback, useState, useMemo, memo } from 'react'
import styled from 'styled-components'
import Creatable from 'react-select/creatable'
import { components } from 'react-select'
import type {
  InputActionMeta,
  ActionMeta,
  OptionTypeBase as OptionType,
  SelectComponentsConfig,
} from 'react-select'
import _ from 'lodash'

import Icon from 'components/Icon'
import theme from 'vars/theme'
import { compareValue } from 'utils/commonFunctions'
import { ReactComponent as LoadingIndicator } from 'assets/svgs/loading.svg'

import { SelectOptionsWrapper as SelectOptionsWrapperBase } from './style'
import { ErrorText } from '../SingleForm/style'

// JS styled component (./style.js) lacks typed custom props; widen with a type-only cast
const SelectOptionsWrapper = SelectOptionsWrapperBase as React.FC<
  React.PropsWithChildren<{
    width?: number | string
    color?: string
    isError?: boolean
    style?: React.CSSProperties
    className?: string
    'data-dg-id'?: string
    'data-csv-upload-id'?: string
  }>
>

export const Label = styled.label<{ isHightLight?: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ isHightLight }) => (isHightLight ? theme.blueMedium2 : '')};

  img {
    margin-left: 5px;
  }
`
const CustomLoading = styled(LoadingIndicator)`
  display: block;
  margin: 10px auto;
`

const MenuList = (props: any) => {
  return (
    <components.MenuList {...props}>
      {props.children}
      {/* @ts-ignore */}
      {props?.isLoading && <CustomLoading />}
    </components.MenuList>
  )
}

type Props = {
  className: string
  options: OptionType[]
  isLoading?: boolean
  isDisabled?: boolean
  errorMessage?: string
  required?: boolean
  label?: string
  isHightLight?: boolean
  labelFontSize?: string
  maxLength?: number
  type?: 'string' | 'number'
  value: string | number
  defaultOption?: OptionType
  // NOTE: react-select 内部要素（control / value-container / dropdown-indicator）へ data-dg-id を
  // 透過するための forward 専用 props。固定値は共通コンポーネント側に持たせず、利用側から渡す
  // （`.claude/rules/data-dg-id.md` の方法 3。内部要素は直書き・DataDgId ラッパでは届かない）
  controlDataDgId?: string
  valueContainerDataDgId?: string
  dropdownIndicatorDataDgId?: string
  onChange: (option: OptionType) => void
  onInputChange: (value: string) => void
  onBlur: (value: string) => void
  onFocus: () => void
  onMenuScrollToBottom: (element: any, inputValue: string) => void
}

const InputAction = {
  INPUT_CHANGE: 'input-change',
} as const

const ActionType = {
  SELECT_OPTION: 'select-option',
  CREATE_OPTION: 'create-option',
} as const

const CreatableSelectLoading: React.FC<Props> = ({
  value,
  options,
  errorMessage = '',
  required,
  label,
  isHightLight,
  labelFontSize,
  maxLength = 200,
  type = 'string',
  defaultOption,
  className,
  controlDataDgId,
  valueContainerDataDgId,
  dropdownIndicatorDataDgId,
  onChange,
  onBlur,
  onMenuScrollToBottom,
  onInputChange,
  ...rest
}) => {
  const [createdOption, setCreatedOption] = useState<OptionType[]>([])
  const [inputValue, setInputValue] = useState<string>('')

  const finalOptions = useMemo<OptionType[]>(
    () =>
      _.uniqBy([...(defaultOption ? [defaultOption] : []), ...createdOption, ...options], 'value'),
    [defaultOption, createdOption, options]
  )

  const selectedValue = useMemo(() => {
    const selected = finalOptions.find(
      (option) => compareValue(option.value, value) || option.label === value
    )
    return selected || null
  }, [finalOptions, value])

  const isValidNewOption = useCallback((inputValue: string) => {
    const val = inputValue.trim()
    return !!val
  }, [])

  const handleChange = useCallback(
    (option: OptionType, { action }: ActionMeta<OptionType>) => {
      const { isDisabled } = rest

      if (isDisabled) return

      switch (action) {
        case ActionType.SELECT_OPTION:
          onChange(option)
          break
        case ActionType.CREATE_OPTION:
          onChange(option)
          setCreatedOption([...createdOption, option])
          break
        default:
          break
      }
    },
    [createdOption, onChange, rest]
  )

  const handleCreate = useCallback(
    (value: string) => {
      const val = value.trim()
      if (val) {
        const option = finalOptions.find((it) => compareValue(val, it.label))
        if (option) {
          handleChange(option, { action: ActionType.SELECT_OPTION })
          return
        }
        const newOption = {
          value: val,
          label: val,
        }
        handleChange(newOption, { action: ActionType.CREATE_OPTION })
      }
    },
    [finalOptions, handleChange]
  )

  const handleBlur = useCallback(
    (event: any) => {
      const value = event?.target?.value || ''

      handleCreate(value)
      onBlur(value)
    },
    [handleCreate, onBlur]
  )

  const handleScrollToBottom = useCallback(
    (event: any) => {
      onMenuScrollToBottom(event, inputValue)
    },
    [inputValue, onMenuScrollToBottom]
  )

  const handleInputChange = useCallback(
    (value: string, { action }: InputActionMeta) => {
      if (action === InputAction.INPUT_CHANGE) {
        onInputChange(value)
      }

      setInputValue(value)
    },
    [onInputChange]
  )

  const selectComponents = useMemo(() => {
    // NOTE: 型を付けて override 名のタイポ（DropDownIndicator 等）を検出する。付け間違えても
    // エラーにならず「属性が付かない」だけで気付けないため
    const overrides: SelectComponentsConfig<OptionType, false> = {
      LoadingIndicator: () => <></>,
      LoadingMessage: () => <></>,
      MenuList,
    }

    // WHY: Control / DropdownIndicator は innerProps を DOM に spread するため、そこへ data-dg-id を差し込む
    // NOTE: `...props.innerProps` を先に置く（後ろに置くと onMouseDown / onTouchEnd を潰して開閉が壊れる）
    if (controlDataDgId) {
      overrides.Control = (props: any) => (
        <components.Control
          {...props}
          innerProps={{ ...props.innerProps, 'data-dg-id': controlDataDgId }}
        />
      )
    }
    if (dropdownIndicatorDataDgId) {
      overrides.DropdownIndicator = (props: any) => (
        <components.DropdownIndicator
          {...props}
          innerProps={{ ...props.innerProps, 'data-dg-id': dropdownIndicatorDataDgId }}
        />
      )
    }
    // WHY: react-select v3 の ValueContainer だけ innerProps を DOM に流さない（3.1.0 / 3.2.0 の
    // 実装を確認済み。props.innerProps を読んでいない）ため、Control / DropdownIndicator と同じ手が
    // 使えず、基底コンポーネントが返す要素を cloneElement して付与している。
    // 自前で div を書き起こす手もあるが emotion の getStyles('valueContainer') を失いレイアウトが崩れる。
    // WARNING: この行は ValueContainer が「hooks を持たない関数コンポーネント」であることに依存する
    // （v3 系 = 3.1.0 / 3.1.1 / 3.2.0 はすべて該当）。v5 では ValueContainer が innerProps を spread する
    // ようになるため、react-select を上げるときは cloneElement を捨てて上の 2 つと同じ形に寄せる。
    // 依存が崩れたら CreatableSelectLoading.test.tsx が落ちる。
    if (valueContainerDataDgId) {
      overrides.ValueContainer = (props: any) =>
        React.cloneElement((components.ValueContainer as any)(props), {
          'data-dg-id': valueContainerDataDgId,
        })
    }

    return overrides
  }, [controlDataDgId, dropdownIndicatorDataDgId, valueContainerDataDgId])

  return (
    <React.Fragment>
      <SelectOptionsWrapper className={className || ' mb-2'} isError={!!errorMessage}>
        {label && (
          <Label
            style={{ fontSize: labelFontSize || '14px' }}
            isHightLight={isHightLight}
            className="label-flex"
          >
            {label}
            {required && <span className="color-red-med1 font-weight-bold">*</span>}
            {/* @ts-ignore */}
            {isHightLight && <Icon src="/assets/svgs/man-user.svg" />}
          </Label>
        )}
        <div className="input-flex flex-column">
          <Creatable
            autosize={false}
            className="w-100"
            classNamePrefix="mono-creatable"
            options={finalOptions}
            value={selectedValue}
            //@ts-ignore
            onChange={handleChange}
            isValidNewOption={isValidNewOption}
            formatCreateLabel={() => ''}
            noOptionsMessage={() => null}
            onBlur={handleBlur}
            onCreateOption={handleCreate}
            blurInputOnSelect={false}
            components={selectComponents}
            onMenuScrollToBottom={handleScrollToBottom}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            {...rest}
          />
          {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
        </div>
      </SelectOptionsWrapper>
    </React.Fragment>
  )
}

export default memo(CreatableSelectLoading)
